import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft, FiTrash2, FiCheck, FiMessageCircle, FiMail, FiPhone, FiCalendar } from 'react-icons/fi'
export default function ContactMessagesPage() {
  const { websiteId } = useParams()
  const navigate = useNavigate()

  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchMessages()
  }, [websiteId, statusFilter, page])

  const fetchMessages = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.append('status', statusFilter)
      params.append('page', page)
      params.append('limit', 10)

      // Ensure Authorization header is present (fallback to tokens in localStorage)
      if (!axios.defaults.headers.common['Authorization']) {
        const token =
          localStorage.getItem('userAdminToken') ||
          localStorage.getItem('userAdminSession') ||
          localStorage.getItem('token') ||
          localStorage.getItem('globalAdminToken')

        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await axios.get(`/contact-messages/website/${websiteId}?${params}`)
      setMessages(response.data.data || [])
      setTotalPages(response.data.pagination?.pages || 1)
      console.log('[ContactMessages] Fetched:', response.data.data?.length, 'messages')
    } catch (error) {
      console.error('[ContactMessages] Error fetching:', error.response?.data || error.message || error)
      const msg = error.response?.data?.message || 'Failed to load contact messages'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await axios.patch(`/contact-messages/${messageId}/status`, { status: newStatus })
      toast.success(`Status updated to "${newStatus}"`)
      
      // Update local state
      setMessages((prev) =>
        prev.map((msg) => (msg._id === messageId ? { ...msg, status: newStatus } : msg))
      )
      if (selectedMessage?._id === messageId) {
        setSelectedMessage((prev) => ({ ...prev, status: newStatus }))
      }
    } catch (error) {
      console.error('[ContactMessages] Error updating status:', error)
      toast.error('Failed to update status')
    }
  }



  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return

    try {
      await axios.delete(`/contact-messages/${messageId}`)
      toast.success('Message deleted')
      
      // Remove from list
      setMessages((prev) => prev.filter((msg) => msg._id !== messageId))
      if (selectedMessage?._id === messageId) {
        setSelectedMessage(null)
      }
    } catch (error) {
      console.error('[ContactMessages] Error deleting:', error)
      toast.error('Failed to delete message')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => navigate('/user-admin/websites')}
            className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
            title="Back to Website Builder Dashboard"
          >
            <FiArrowLeft className="w-4 sm:w-5 h-4 sm:h-5" />
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
            <FiMessageCircle className="text-blue-600 flex-shrink-0 w-5 sm:w-6 h-5 sm:h-6" />
            <span className="truncate">Contact Messages</span>
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              {/* Filter Buttons */}
              <div className="p-3 sm:p-4 border-b border-gray-200">
                <div className="space-y-1.5 sm:space-y-2">
                  {['all', 'new', 'read', 'archived'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status)
                        setPage(1)
                      }}
                      className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-lg capitalize transition text-xs sm:text-sm ${
                        statusFilter === status
                          ? 'bg-blue-100 text-blue-900 font-semibold'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {status === 'all' ? 'All Messages' : status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Messages */}
              {messages.length > 0 ? (
                <>
                  <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                    {messages.map((msg) => (
                      <button
                        key={msg._id}
                        onClick={() => setSelectedMessage(msg)}
                        className={`w-full text-left p-3 sm:p-4 hover:bg-gray-50 transition border-l-4 ${
                          selectedMessage?._id === msg._id
                            ? 'border-l-blue-600 bg-blue-50'
                            : msg.status === 'new'
                            ? 'border-l-yellow-400'
                            : msg.status === 'archived'
                            ? 'border-l-gray-400'
                            : 'border-l-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-gray-900 text-xs sm:text-sm truncate">
                          {msg.visitorName}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{msg.visitorEmail}</div>
                        <div className="mt-1 text-xs sm:text-sm text-gray-700 truncate">{msg.subject}</div>
                        <div className="mt-2 flex items-center justify-between gap-1">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 sm:py-1 rounded capitalize ${
                              msg.status === 'new'
                                ? 'bg-yellow-100 text-yellow-800'
                                : msg.status === 'read'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {msg.status}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {new Date(msg.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-3 sm:p-4 border-t border-gray-200 flex gap-1 sm:gap-2 justify-center flex-wrap">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition"
                      >
                        Prev
                      </button>
                      <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-gray-700">
                        {page} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-6 sm:p-8 text-center text-xs sm:text-sm text-gray-500">
                  No messages yet
                </div>
              )}
            </div>
          </div>

          {/* Message Detail */}
          {selectedMessage ? (
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-200">
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900 break-words">{selectedMessage.subject}</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">From {selectedMessage.visitorName}</p>
                  </div>
                  <span
                    className={`text-xs sm:text-sm font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded capitalize flex-shrink-0 ${
                      selectedMessage.status === 'new'
                        ? 'bg-yellow-100 text-yellow-800'
                        : selectedMessage.status === 'read'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedMessage.status}
                  </span>
                </div>

                {/* Contact Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <FiMail className="w-4 h-4" />
                      Email
                    </label>
                    <a
                      href={`mailto:${selectedMessage.visitorEmail}`}
                      className="text-blue-600 hover:underline"
                    >
                      {selectedMessage.visitorEmail}
                    </a>
                  </div>
                  {selectedMessage.visitorPhone && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <FiPhone className="w-4 h-4" />
                        Phone
                      </label>
                      <a
                        href={`tel:${selectedMessage.visitorPhone}`}
                        className="text-blue-600 hover:underline"
                      >
                        {selectedMessage.visitorPhone}
                      </a>
                    </div>
                  )}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                      <FiCalendar className="w-4 h-4" />
                      Date
                    </label>
                    <p className="text-gray-900">
                      {new Date(selectedMessage.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Message Content */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Message</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {selectedMessage.message}
                    </p>
                  </div>
                </div>

                {/* Metadata */}
                {selectedMessage.metadata && (
                  <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded border border-gray-200">
                    <p>IP: {selectedMessage.metadata.ipAddress || 'N/A'}</p>
                    <p>Referrer: {selectedMessage.metadata.referrer || 'Direct'}</p>
                  </div>
                )}



                {/* Status Actions */}
                <div className="pt-6 border-t border-gray-200 space-y-3">
                  <h3 className="font-semibold text-gray-900">Actions</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {['read', 'archived'].map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(selectedMessage._id, status)}
                        disabled={selectedMessage.status === status}
                        className={`py-2 px-3 rounded-lg font-medium text-sm transition capitalize ${
                          selectedMessage.status === status
                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        Mark as {status}
                      </button>
                    ))}
                    <button
                      onClick={() => handleDeleteMessage(selectedMessage._id)}
                      className="py-2 px-3 rounded-lg font-medium text-sm bg-red-600 hover:bg-red-700 text-white transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="lg:col-span-2 bg-white rounded-lg shadow p-8 flex items-center justify-center h-96">
              <p className="text-gray-500 text-center">
                Select a message to view details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
